import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { BehaviorSubject, from } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from 'rxjs/operators';

import SEARCH_USERS_BY_CORP_QUERY from './graphql/SearchUsersByCorpQuery.v4.b2b.graphql';
import type {
  SearchUsersByCorpQuery,
  SearchUsersByCorpQueryVariables,
} from './graphql/types/SearchUsersByCorpQuery';

const _searchResultBehavior = new BehaviorSubject<string>('');
const searchResultObservable$ = _searchResultBehavior.asObservable();

const format = ({ data }: { data?: SearchUsersByCorpQuery }) =>
  data?.organizationUsers?.edges?.map(user => {
    const userNode = user?.node;
    const teamName =
      userNode?.teamId && userNode?.teams
        ? userNode.teams.find(t => t?.teamId === userNode.teamId)?.team?.name
        : null;

    return {
      id: userNode?.user?.originalId || null,
      title: userNode?.user?.fullName || userNode?.user?.username || '',
      subTitle: teamName ? `Ruta ${teamName}` : '',
      imageUrl: userNode?.user?.profilePictureLink?.url || '',
      url: `/org/${userNode?.organizationSlug}/panel/estadisticas/especificas/${userNode?.user?.username}/`,
      type: 'user',
    };
  }) || [];

const useSearchCorpUsers = ({
  searchKey,
  slug,
  itemsPerFetch,
}: {
  searchKey: string;
  slug: string;
  itemsPerFetch: number;
}) => {
  const { data, loading, refetch } = useQuery<
    SearchUsersByCorpQuery,
    SearchUsersByCorpQueryVariables
  >(SEARCH_USERS_BY_CORP_QUERY, {
    context: { clientName: 'v4.b2b' },
    notifyOnNetworkStatusChange: true,
    variables: {
      slug,
      searchKey: '',
      first: itemsPerFetch,
      usersCorp: false,
    },
    skip: !searchKey || !slug,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    _searchResultBehavior.next(searchKey);
    const subject = searchResultObservable$
      .pipe(
        filter(value => value.length > 1),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() =>
          from(
            refetch({
              searchKey,
              first: itemsPerFetch,
              slug,
            }),
          ),
        ),
      )
      .subscribe();

    return () => {
      subject.unsubscribe();
    };
  }, [searchKey]);

  return {
    userResults: format({ data }),
    loadingUsers: loading,
    totalUsersCount: data?.organizationUsers?.totalCount || 0,
  };
};

export default useSearchCorpUsers;
